import * as React from 'react';

import cx from 'classnames';

import { DashedBox } from '~shared/components/DashedBox/DashedBox';

import { useDropzone } from './hooks/useDropzone';

export interface FileDropInputProps {
  onFilesSelected: (filesAdded: Array<File>) => void;
  isDisabled?: boolean;
  children: React.ReactNode;
  acceptedFileTypes: Array<string>;
  maxFileCount: number;
  currentFilesCount: number;
}

export function FileDropInput({
  maxFileCount: maxFiles,
  currentFilesCount,
  onFilesSelected,
  isDisabled = false,
  children,
  acceptedFileTypes,
}: FileDropInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onFilesSelected,
    isDisabled,
    acceptedFileTypes,
    maxFileCount: maxFiles,
    currentFilesCount,
  });

  const handleClick = React.useCallback(() => {
    if (isDisabled) return;
    inputRef.current?.click();
  }, [isDisabled]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDisabled) return;
      // Trigger file picker on Enter or Space
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        inputRef.current?.click();
      }
    },
    [isDisabled]
  );

  const className = cx(
    'file-drop-dashed-border',
    'group relative',
    'px-4 py-2',
    'transition-colors cursor-pointer',
    'text-grey-900',
    'focus-visible:text-cyan-600 focus-visible:outline-none',
    {
      'text-cyan-600': isDragActive,
      'cursor-not-allowed opacity-50': isDisabled,
    }
  );

  return (
    <DashedBox
      borderWidth={isDragActive ? 2 : 1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-label="Upload file"
      aria-disabled={isDisabled}
      className={className}
      {...getRootProps()}
    >
      {/* Since the text, and the border have different hover effects, we have to
          use font-color here to prevent the border from being the same colour as the text */}
      <div className="text-cyan-700 hover:text-cyan-600">
        <input {...getInputProps()} ref={inputRef} disabled={isDisabled} />
        {children}
      </div>
    </DashedBox>
  );
}
